import { useTranslation } from 'react-i18next';

import {
  Form,
  SelectInput,
  NumberInput,
  TextInput,
  CountrySelect,
  DatePickerInput,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';

import {
  GendersEnum,
  SalutationOptionsEnum,
  DEFAULT_NAME_FIELD_REGEX,
  STREET_FIELD_REGEX,
  HOUSE_NUMBER_FIELD_REGEX,
} from '@thinknet-gmbh/platform-common';

import { PersonalInfoRow } from '../../personal-info.styles';
import { StyledContainer, TitleRowLabel } from '../../../employee-common.styles';

const PersonalData = ({
  isEdit,
  isOwnData,
  profile,
}: {
  isEdit?: boolean;
  isOwnData?: boolean;
  employeeId?: string;
  profile: {
    gender?: string;
    birthday?: string;
  };
}) => {
  const { t, i18n } = useTranslation(['common', 'employee']);

  return (
    <StyledContainer>
      <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('employee:labels.personalData')}
      </TitleRowLabel>

      <PersonalInfoRow>
        <Form.Item
          name={'salutation'}
          label={t('employee:employeePersonalInfo.labels.salutation')}
          rules={[
            {
              required: true,
              message: t('customers:error.fieldIsRequired'),
            },
          ]}
        >
          <SelectInput disabled={isOwnData} placeholder={t('employee:employeePersonalInfo.labels.salutationOptions.MR')}>
            {Object.keys(SalutationOptionsEnum).map(option => (
              <SelectInput.Option value={option} key={option}>
                {t(`employee:employeePersonalInfo.labels.salutationOptions.${option}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name={'firstName'}
          label={t('common:labels.firstName')}
          rules={[
            { required: true, message: t('common:validation.error.firstNameRequired') },
            {
              max: 30,
              message: t('common:validation.error.maxLength', { maxLength: 30 }),
            },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput disabled={isOwnData} placeholder="Kate" maxLength={30} />
        </Form.Item>
        <Form.Item
          name={'lastName'}
          label={t('common:labels.lastName')}
          rules={[
            { required: true, message: t('common:validation.error.lastNameRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
            {
              max: 30,
              message: t('common:validation.error.maxLength', { maxLength: 30 }),
            },
          ]}
        >
          <TextInput disabled={isOwnData} placeholder="Olov" maxLength={30} />
        </Form.Item>
        <Form.Item
          name={'gender'}
          label={t('employee:employeePersonalInfo.labels.gender')}
          rules={[
            {
              required: true,
              message: t('employee:employeePersonalInfo.validation.genderRequired'),
            },
          ]}
        >
          <SelectInput
            placeholder={t('employee:employeePersonalInfo.labels.female')}
            disabled={isEdit && !!profile?.gender}
          >
            {Object.keys(GendersEnum).map(gender => (
              <SelectInput.Option value={gender} key={gender}>
                {t(`employee:employeePersonalInfo.labels.${gender.toLowerCase()}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
      </PersonalInfoRow>

      <PersonalInfoRow>
        <Form.Item
          name={['employeePersonalInfo', 'academicDegree']}
          label={t('employee:employeePersonalInfo.labels.academicDegree')}
        >
          <TextInput
            placeholder={t('employee:employeePersonalInfo.labels.professor')}
            maxLength={20}
            disabled={isOwnData}
          />
        </Form.Item>
        <Form.Item
          name={['address', 'street']}
          label={t('common:labels.address.street')}
          rules={[
            { required: true, message: t('customers:error.fieldIsRequired') },
            {
              pattern: STREET_FIELD_REGEX,
              message: t('common:validation.error.streetFormat'),
            },
          ]}
        >
          <TextInput placeholder={t('common:placeholders.street')} maxLength={34} />
        </Form.Item>

        <Form.Item
          name={['address', 'houseNumber']}
          label={t('employee:employeePersonalInfo.labels.houseNumber')}
          rules={[
            { required: true, message: t('customers:error.fieldIsRequired') },
            {
              pattern: HOUSE_NUMBER_FIELD_REGEX,
              message: t('common:validation.error.houseNumberFormat'),
            },
          ]}
        >
          <TextInput placeholder="27" maxLength={9} />
        </Form.Item>

        <Form.Item
          name={['address', 'zipCode']}
          label={t('common:labels.address.zipCode')}
          rules={[
            { required: true, message: t('customers:error.fieldIsRequired') },
            { type: 'number', message: t('common:validation.error.mustBeInteger') },
            {
              validator: (_: any, value: any) =>
                Number.isInteger(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('common:validation.error.mustBeInteger'))),
            },
          ]}
        >
          <NumberInput
            thousandSeparator={false}
            allowLeadingZeros={false}
            placeholder="27321"
            maxLength={5}
          />
        </Form.Item>

        <Form.Item
          name={['address', 'city']}
          label={t('common:labels.address.city')}
          rules={[{ required: true, message: t('customers:error.fieldIsRequired') }]}
        >
          <TextInput placeholder="Hamburg" maxLength={34} />
        </Form.Item>
        <Form.Item
          name={['address', 'country']}
          label={t('common:labels.address.country')}
          rules={[{ required: true, message: t('customers:error.fieldIsRequired') }]}
        >
          <CountrySelect t={t} placeholder={t('common:placeholders.country')} maxLength={50} />
        </Form.Item>
      </PersonalInfoRow>

      <PersonalInfoRow>
        <Form.Item
          name={'birthday'}
          label={t('employee:labels.birthday')}
          rules={[{ required: true, message: t('employee:validationError.birthdayRequired') }]}
        >
          <DatePickerInput
            lang={i18n.language}
            format="DD.MM.YYYY"
            disabled={isEdit && !!profile?.birthday}
          />
        </Form.Item>
        <Form.Item name={'birthName'} label={t('employee:employeePersonalInfo.labels.birthName')}>
          <TextInput disabled={isOwnData} placeholder="Kate" maxLength={30} />
        </Form.Item>
        <Form.Item
          name={'cityOfBirth'}
          label={t('employee:employeePersonalInfo.labels.cityOfBirth')}
        >
          <TextInput disabled={isOwnData} placeholder="Hamburg" maxLength={34} />
        </Form.Item>
        <Form.Item
          name={'countryOfBirth'}
          label={t('employee:employeePersonalInfo.labels.countryOfBirth')}
        >
          <CountrySelect disabled={isOwnData} t={t} placeholder={t('common:placeholders.country')} maxLength={50} />
        </Form.Item>
        <Form.Item
          name={'nationality'}
          label={t('employee:labels.nationality')}
          rules={[
            { required: true, message: t('employee:validationError.nationalityRequired') },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <CountrySelect disabled={isOwnData} t={t} placeholder={t('common:placeholders.country')} maxLength={50} />
        </Form.Item>
      </PersonalInfoRow>
    </StyledContainer>
  );
};

export default PersonalData;
